import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import { LockOutlined, PhoneOutlined, ArrowBack } from '@mui/icons-material';
import { useIntl } from 'react-intl';
import { useCustomerAuth } from '../CustomerAuthContext';
import { useLoginWithPinMutation, useRequestPinResetMutation } from '../queries';
import { loginLookup, LoginLookupEntry } from '../api';

export const PhoneLoginPage = () => {
  const navigate = useNavigate();
  const intl = useIntl();
  const { login, isAuthenticated, loading: authLoading } = useCustomerAuth();

  const [phone, setPhone] = useState('');
  const [pin, setPin] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [formInfo, setFormInfo] = useState<string | null>(null);

  // Step: 'phone' | 'pin'
  const [step, setStep] = useState<'phone' | 'pin'>('phone');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [hairdressers, setHairdressers] = useState<LoginLookupEntry[]>([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedHairdresserName, setSelectedHairdresserName] = useState('');

  const { mutateAsync: loginMutate } = useLoginWithPinMutation();
  const [isPending, setIsPending] = useState(false);
  const { mutate: requestReset, isPending: resetPending } = useRequestPinResetMutation();

  if (authLoading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/customer-reservations/booking" replace />;
  }

  // Step 1: phone entered, look up hairdresser
  const handlePhoneNext = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const normalizedPhone = phone.trim().replace(/[\s\-().+]/g, '');
    if (!normalizedPhone) {
      setFormError(intl.formatMessage({ id: 'customerAuth.loginPhoneRequired' }));
      return;
    }

    setLookupLoading(true);
    try {
      const results = await loginLookup(normalizedPhone);
      if (results.length === 0) {
        setFormError(intl.formatMessage({ id: 'customerAuth.phoneNotFound' }));
      } else if (results.length === 1) {
        setSelectedUserId(results[0].userId);
        setSelectedHairdresserName(results[0].hairdresserName);
        setStep('pin');
      } else {
        setHairdressers(results);
        setStep('pin'); // show picker inside pin step
      }
    } catch {
      setFormError(intl.formatMessage({ id: 'customerAuth.loginFailed' }));
    } finally {
      setLookupLoading(false);
    }
  };

  // Step 2: PIN submitted
  const handlePinSubmit = async (e: React.FormEvent) => {
    if (isPending) {
      return;
    }
    e.preventDefault();
    setFormError(null);

    if (!selectedUserId) {
      setFormError(intl.formatMessage({ id: 'customerAuth.selectHairdresser' }));
      return;
    }
    if (!pin.trim()) {
      setFormError(intl.formatMessage({ id: 'customerAuth.loginPinRequired' }));
      return;
    }

    setIsPending(true);
    try {
      const response = await loginMutate({
        phone: phone.trim().replace(/[\s\-().+]/g, ''),
        userId: selectedUserId,
        pin: pin.trim(),
      });
      const session = await login();
      if (!session.valid || session.userId !== selectedUserId || session.clientId !== response.clientId) {
        setFormError(intl.formatMessage({
          id: 'customerLogin.sessionFailed',
          defaultMessage: 'Přihlášení se nepodařilo dokončit. Povolte cookies pro tento web a zkuste to znovu. Pokud používáte prohlížeč v jiné aplikaci, otevřete odkaz v běžném prohlížeči.',
        }));
        return;
      }
      navigate('/customer-reservations/booking', { replace: true });
    } catch (error) {
      setFormError(error instanceof Error ? error.message : intl.formatMessage({ id: 'customerLogin.loginFailed' }));
    } finally {
      setIsPending(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Stack spacing={3}>
          <Typography variant="h5" textAlign="center">
            {intl.formatMessage({ id: 'customerAuth.loginTitle' })}
          </Typography>

          {formError && (
            <Alert severity="error" onClose={() => setFormError(null)}>
              {formError}
            </Alert>
          )}
          {formInfo && (
            <Alert severity="success" onClose={() => setFormInfo(null)}>
              {formInfo}
            </Alert>
          )}

          {/* Step 1: phone */}
          {step === 'phone' && (
            <form onSubmit={handlePhoneNext}>
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  {intl.formatMessage({ id: 'customerAuth.loginPhoneStep' })}
                </Typography>
                <TextField
                  label={intl.formatMessage({ id: 'customerAuth.phoneLabel' })}
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  type="tel"
                  autoComplete="tel"
                  inputMode="numeric"
                  fullWidth
                  autoFocus
                  disabled={lookupLoading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneOutlined />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={lookupLoading}
                  startIcon={
                    lookupLoading ? <CircularProgress size={20} color="inherit" /> : undefined
                  }
                >
                  {intl.formatMessage({ id: 'customerAuth.loginContinue' })}
                </Button>
              </Stack>
            </form>
          )}

          {/* Step 2: optional hairdresser picker + PIN */}
          {step === 'pin' && (
            <form onSubmit={handlePinSubmit}>
              <Stack spacing={2}>
                {/* Multiple hairdressers — let user pick */}
                {hairdressers.length > 1 && !selectedUserId && (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      {intl.formatMessage({ id: 'customerAuth.selectHairdresserPrompt' })}
                    </Typography>
                    <List dense disablePadding>
                      {hairdressers.map(h => (
                        <ListItemButton
                          key={h.userId}
                          selected={selectedUserId === h.userId}
                          onClick={() => {
                            setSelectedUserId(h.userId);
                            setSelectedHairdresserName(h.hairdresserName);
                          }}
                          sx={{
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            mb: 0.5,
                          }}
                        >
                          <ListItemText primary={h.hairdresserName} />
                        </ListItemButton>
                      ))}
                    </List>
                  </>
                )}

                {/* Show confirmed hairdresser name */}
                {selectedHairdresserName && (
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    {intl.formatMessage(
                      { id: 'customerAuth.loginPinStep' },
                      { hairdresserName: selectedHairdresserName },
                    )}
                  </Typography>
                )}

                <TextField
                  label={intl.formatMessage({ id: 'customerAuth.pinLabel' })}
                  value={pin}
                  onChange={e => setPin(e.target.value)}
                  type="password"
                  autoComplete="current-password"
                  fullWidth
                  autoFocus
                  disabled={isPending}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlined />
                        </InputAdornment>
                      ),
                    },
                  }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={isPending || (hairdressers.length > 1 && !selectedUserId)}
                  startIcon={isPending ? <CircularProgress size={20} color="inherit" /> : undefined}
                >
                  {intl.formatMessage({ id: 'customerAuth.loginButton' })}
                </Button>

                <Button
                  variant="text"
                  disabled={!selectedUserId || resetPending}
                  onClick={() =>
                    requestReset(
                      { phone: phone.trim().replace(/[\s\-().+]/g, ''), userId: selectedUserId },
                      {
                        onSuccess: data => {
                          setFormError(null);
                          setFormInfo(data.message);
                        },
                        onError: error => setFormError(error.message),
                      },
                    )
                  }
                >
                  {intl.formatMessage({ id: 'customerLogin.forgotPin' })}
                </Button>

                <Button
                  variant="text"
                  size="small"
                  startIcon={<ArrowBack />}
                  onClick={() => {
                    setStep('phone');
                    setPin('');
                    setFormError(null);
                    setSelectedUserId('');
                    setHairdressers([]);
                  }}
                >
                  {intl.formatMessage({ id: 'customerAuth.loginBack' })}
                </Button>
              </Stack>
            </form>
          )}

          <Typography variant="caption" color="text.secondary" textAlign="center">
            {intl.formatMessage({ id: 'customerAuth.loginHint' })}
          </Typography>
        </Stack>
      </Paper>
    </Container>
  );
};
